import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const USERS_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/users';

const OFFICER_TITLES = {
  admin:     'Finance Chairperson & System Administrator',
  finance:   'Treasurer',
  secretary: 'Secretary General'
};

function Documents() {
  const [activeDoc, setActiveDoc] = useState('mdn');
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [memberData, setMemberData] = useState(null);

  const [officers, setOfficers] = useState([]);
  const [showOfficerModal, setShowOfficerModal] = useState(false);
  const [selectedOfficerId, setSelectedOfficerId] = useState('');
  const [pendingDoc, setPendingDoc] = useState(null);

  const [mdnForm, setMdnForm]   = useState({ obligation_type: '', due_date_missed: '', amount: '', previous_balance: '', notes: '' });
  const [pcnForm, setPcnForm]   = useState({ previous_balance: '', previous_balance_words: '', amount_paid: '', amount_paid_words: '', receipt_no: '', payment_date: '', result_status: 'cleared', remaining_balance: '', remaining_balance_words: '', remarks: '' });
  const [mcnForm, setMcnForm]   = useState({ credit_note_no: '', amount: '', amount_words: '', credit_type: 'Overpayment', payment_method: 'Cash', balance_after: '', balance_after_words: '', remarks: '' });
  const [cunForm, setCunForm]   = useState({ credit_note_ref: '', credit_note_date: '', credit_amount: '', obligation_desc: '', obligation_date: '', obligation_amount: '', result: 'credit_equals', balance_after: '' });
  const [receiptForm, setReceiptForm] = useState({ receipt_no: '', amount: '', amount_words: '', purpose: '', payment_method: 'Cash / Mobile Money' });
  const [mbaForm, setMbaForm]   = useState({ national_id: '', signed_date: '' });
  const [loanForm, setLoanForm] = useState({ loan_amount: '', repayment_option: 'B', term_months: '', total_dev_contribution: '', total_due: '', monthly_instalment: '', due_day: '', final_date: '', disbursement_date: '', security_external: '' });
  const [clearanceForm, setClearanceForm] = useState({ reason: 'resignation', issued_by: '', ratified_by: '', issue_date: '' });
  const [roscaSwapForm, setRoscaSwapForm] = useState({ member_a_name: '', member_a_id: '', member_a_phone: '', member_b_name: '', member_b_id: '', member_b_phone: '', member_a_position: '', member_b_position: '', cycle_ref: '', swap_date: '' });
  const [roscaTransferForm, setRoscaTransferForm] = useState({ transferee_name: '', transferee_id: '', transferee_phone: '', position: '', monthly_contribution: '', cycle_ref: '', transfer_date: '', reason: '' });
  const [savingsForm, setSavingsForm] = useState({ cycle_ref: '', cycle_start: '', cycle_end: '', monthly_amount: '', due_day: '', expected_payout_date: '', total_positions: '', member_position: '', bank_name: '', account_name: '', account_number: '', signed_date: '' });
  const [lccForm, setLccForm]   = useState({ position_title: '', election_date: '', term_start: '', term_end: '', agreement_date: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    api.get('/api/members', { headers }).then(r => setMembers(r.data));
    axios.get(`${USERS_API}/officers`, { headers }).then(r => {
      setOfficers(r.data);
      const withSig = r.data.filter(o => o.signature_image);
      if (withSig.length > 0) setSelectedOfficerId(String(withSig[0].id));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedMember) {
      const m = members.find(m => m.id === parseInt(selectedMember));
      setMemberData(m);
    }
  }, [selectedMember, members]);

  const openOfficerModal = (docFn) => { setPendingDoc(() => docFn); setShowOfficerModal(true); };
  const confirmAndGenerate = () => { const officer = officers.find(o => String(o.id) === String(selectedOfficerId)) || null; setShowOfficerModal(false); if (pendingDoc) pendingDoc(officer); };
  const skipOfficer = () => { setShowOfficerModal(false); if (pendingDoc) pendingDoc(null); };

  const getRefNumber = (prefix) => `${prefix}-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`;
  const today = () => new Date().toLocaleDateString('en-GB');

  // ── SHARED HELPERS ──────────────────────────────────────────────────────

  const addKAHeader = (doc, title, subtitle = '') => {
    doc.setFillColor(44, 62, 80);
    doc.rect(0, 0, 210, subtitle ? 32 : 26, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text('KIGEZI ASSOCIATION', 105, 11, { align: 'center' });
    doc.setFontSize(11); doc.setFont('helvetica', 'normal');
    doc.text(title, 105, 20, { align: 'center' });
    if (subtitle) { doc.setFontSize(9); doc.text(subtitle, 105, 28, { align: 'center' }); }
    doc.setTextColor(0, 0, 0);
    return subtitle ? 38 : 32;
  };

  const addFooter = (doc) => {
    const ph = doc.internal.pageSize.height;
    doc.setFillColor(44, 62, 80); doc.rect(0, ph - 12, 210, 12, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(7); doc.setFont('helvetica', 'normal');
    doc.text('Kigezi Association — Kira Municipality, Kyariwajala Ward, Namugongo Cell — Official Document', 105, ph - 4, { align: 'center' });
  };

  const sigLine = (doc, x, y, width, label, name = '', sub = '', imgData = null) => {
    if (imgData) {
      try {
        const fmt = imgData.includes('data:image/png') ? 'PNG' : 'JPEG';
        doc.setFillColor(255, 255, 255); doc.rect(x, y - 11, 40, 10, 'F');
        doc.addImage(imgData, fmt, x, y - 11, 38, 9);
      } catch (e) {}
    }
    doc.setDrawColor(80, 80, 80); doc.setLineWidth(0.3);
    doc.line(x, y, x + width, y);
    doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(0,0,0);
    doc.text(label, x, y + 4);
    if (name) { doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.text(name, x, y + 9); doc.setFont('helvetica', 'normal'); }
    if (sub)  { doc.setTextColor(80,80,80); doc.setFontSize(7); doc.text(sub, x, y + 13); doc.setTextColor(0,0,0); }
    doc.setFontSize(7); doc.line(x, sub ? y + 20 : y + 15, x + width, sub ? y + 20 : y + 15); doc.text('Date:', x, sub ? y + 24 : y + 19);
  };

  const para = (doc, y, text, indent = 15, maxW = 180) => {
    doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0,0,0);
    const lines = doc.splitTextToSize(text, maxW);
    doc.text(lines, indent, y);
    return y + lines.length * 5 + 2;
  };

  const sectionHead = (doc, y, text) => {
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44,62,80);
    doc.text(text, 15, y);
    doc.setTextColor(0,0,0); doc.setFont('helvetica', 'normal');
    return y + 6;
  };

  // ── APPENDIX 7: MEMBER DEBIT NOTE ───────────────────────────────────────
  const generateMDN = (officer) => {
    if (!memberData) return alert('Select a member first');
    const doc = new jsPDF();
    const ref = getRefNumber('MDN');
    let y = addKAHeader(doc, 'MEMBER DEBIT NOTE (MDN)');

    // Ref and date line
    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text(`MDN Ref: ${ref}`, 15, y + 4);
    doc.text(`Date: ${today()}`, 195, y + 4, { align: 'right' });
    y += 10;

    // Section 1: Member Details
    y = sectionHead(doc, y, '1. Member Details');
    doc.setDrawColor(200,200,200); doc.setLineWidth(0.3);
    doc.rect(15, y, 180, 22);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text(`Member Name: ${memberData.full_name}`, 18, y + 6);
    doc.text(`Membership ID / Number: ${memberData.member_number}`, 18, y + 12);
    doc.text(`Contact (Phone/Email): ${memberData.phone || 'N/A'} / ${memberData.email || 'N/A'}`, 18, y + 18);
    y += 26;

    // Section 2: Description of Obligation
    y = sectionHead(doc, y, '2. Description of Obligation');
    doc.rect(15, y, 180, 16);
    doc.text(`Nature of Obligation: ${mdnForm.obligation_type}`, 18, y + 6);
    doc.text(`Due Date Missed: ${mdnForm.due_date_missed}`, 18, y + 12);
    y += 20;

    // Section 3: Reason for Debit
    y = sectionHead(doc, y, '3. Reason for Debit');
    y = para(doc, y, 'Your Member Individual Financial Ledger (MIFL) will be debited with the amount indicated until the obligation is cleared.', 15, 180);
    y += 2;

    // Section 4: Financial Standing Summary
    y = sectionHead(doc, y, '4. Financial Standing Summary');
    autoTable(doc, {
      startY: y,
      body: [
        ['Previous Outstanding Balance', '', `UGX  ${Number(mdnForm.previous_balance || 0).toLocaleString()}`],
        ['Current Debit Amount', '', `UGX  ${Number(mdnForm.amount || 0).toLocaleString()}`],
        ['New Total Outstanding Balance', '', `UGX  ${(parseFloat(mdnForm.amount || 0) + parseFloat(mdnForm.previous_balance || 0)).toLocaleString()}`],
      ],
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 90 }, 1: { cellWidth: 40 }, 2: { fontStyle: 'bold', halign: 'right' } },
      margin: { left: 15, right: 15 },
    });
    y = doc.lastAutoTable.finalY + 4;

    // Section 5: Notes / Remarks
    y = sectionHead(doc, y, '5. Notes / Remarks');
    doc.rect(15, y, 180, 16);
    doc.setFontSize(9);
    doc.text(mdnForm.notes || '', 18, y + 6);
    
    y += 20;

    // Section 6: Authorization
    y = sectionHead(doc, y, '6. Authorization');
    const colW = 85;
    doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    doc.text('Name & Signature', 15 + colW * 0, y); doc.text('Date', 15 + colW * 0 + 55, y);
    doc.text('Name & Signature', 15 + colW * 1, y); doc.text('Date', 15 + colW * 1 + 55, y);
    y += 4;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.text('Prepared by:', 15, y + 2);
    doc.text('Member (Optional):', 15 + colW, y + 2);
    y += 6;

    if (officer?.signature_image) {
      try {
        const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG';
        doc.addImage(officer.signature_image, fmt, 15, y, 38, 9);
      } catch(e) {}
    }
    doc.setDrawColor(80,80,80); doc.line(15, y + 12, 95, y + 12); doc.line(100, y + 12, 180, y + 12);
    doc.setFontSize(7);
    if (officer) { doc.setFont('helvetica', 'bold'); doc.text(officer.full_name || officer.username, 15, y + 16); doc.setFont('helvetica', 'normal'); doc.text(OFFICER_TITLES[officer.role] || '', 15, y + 20); }
    doc.text(`Date: ${today()}`, 15, y + 24);
    doc.line(100, y + 12, 180, y + 12); doc.text('Date:', 100, y + 16);
    y += 30;

    // Important note
    doc.setFillColor(255, 248, 220); doc.rect(15, y, 180, 14, 'F');
    doc.setFontSize(8); doc.setFont('helvetica', 'italic'); doc.setTextColor(100, 60, 0);
    doc.text('Important: This debit note serves as a formal record and will remain active in the Association\'s books until the payment is', 18, y + 5);
    doc.text('cleared. A receipt will be issued upon full settlement.', 18, y + 10);
    doc.setTextColor(0,0,0);

    addFooter(doc);
    doc.save(`MDN-${memberData.member_number}-${ref}.pdf`);
  };

  // ── APPENDIX 8: MEMBER CREDIT NOTE ─────────────────────────────────────
  const generateMCN = (officer) => {
    if (!memberData) return alert('Select a member first');
    const doc = new jsPDF();
    const ref = mcnForm.credit_note_no || getRefNumber('MCN');
    let y = addKAHeader(doc, 'MEMBER CREDIT NOTE');

    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text(`Credit Note No: ${ref}`, 15, y + 4);
    doc.text(`Date: ${today()}`, 195, y + 4, { align: 'right' });
    y += 12;

    // Member info
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    const fields = [
      ['Member Name', memberData.full_name],
      ['Membership ID / Number', memberData.member_number],
      ['Contact (Phone/Email)', `${memberData.phone || 'N/A'} / ${memberData.email || 'N/A'}`],
      ['Amount Credited (UGX)', Number(mcnForm.amount || 0).toLocaleString()],
      ['Amount in words', mcnForm.amount_words || ''],
      ['Credit Type', `${mcnForm.credit_type}`],
      ['Payment Method', mcnForm.payment_method],
      ['Balance after this credit (UGX)', Number(mcnForm.balance_after || 0).toLocaleString()],
      ['Amount in words', mcnForm.balance_after_words || ''],
      ['Remarks', mcnForm.remarks || ''],
    ];
    autoTable(doc, {
      startY: y,
      body: fields,
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 70 } },
      margin: { left: 15, right: 15 },
    });
    y = doc.lastAutoTable.finalY + 6;

    y = para(doc, y, `${memberData.full_name} has overpaid. The credited amount will be applied towards future obligations and tracked through the MIFL.`, 15, 180);
    y += 6;

    // Issued By / Received By
    if (officer?.signature_image) {
      try { const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG'; doc.addImage(officer.signature_image, fmt, 15, y, 38, 9); } catch(e) {}
    }
    y += 12;
    doc.setDrawColor(80,80,80); doc.line(15, y, 95, y); doc.line(110, y, 195, y);
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text(`Issued By: ${officer ? (officer.full_name || officer.username) : ''}`, 15, y + 5);
    doc.text(`Date: ${today()}`, 15, y + 10);
    doc.text('Received By:', 110, y + 5);
    doc.text('Date:', 110, y + 10);

    addFooter(doc);
    doc.save(`MCN-${memberData.member_number}-${ref}.pdf`);
  };

  // ── APPENDIX 9: PAYMENT CONFIRMATION NOTE ──────────────────────────────
  const generatePCN = (officer) => {
    if (!memberData) return alert('Select a member first');
    const doc = new jsPDF();
    const ref = getRefNumber('PCN');
    let y = addKAHeader(doc, 'Payment Confirmation Note');

    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text(`Reference No.: ${ref}`, 15, y + 4);
    doc.text(`Date Issued: ${today()}`, 195, y + 4, { align: 'right' });
    y += 12;

    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    const rows = [
      ['Member Name', memberData.full_name],
      ['Membership ID (if applicable)', memberData.member_number],
      ['Previous Outstanding Balance (UGX)', Number(pcnForm.previous_balance || 0).toLocaleString()],
      ['Amount in words', pcnForm.previous_balance_words || ''],
      ['Amount Paid (UGX)', Number(pcnForm.amount_paid || 0).toLocaleString()],
      ['Amount In words', pcnForm.amount_paid_words || ''],
      ['Receipt No.', pcnForm.receipt_no || ''],
      ['Date of Payment', pcnForm.payment_date || ''],
    ];
    autoTable(doc, {
      startY: y, body: rows,
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
      margin: { left: 15, right: 15 },
    });
    y = doc.lastAutoTable.finalY + 6;

    // Updated Account Status
    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text('Updated Account Status:', 15, y); y += 6;
    doc.setFont('helvetica', 'normal');

    const statuses = [
      { key: 'cleared',       label: 'Cleared - Member now has a zero balance' },
      { key: 'overpaid',      label: 'Overpaid - Member now has a positive balance' },
      { key: 'partially_paid',label: `Partially Paid - Member still owes a balance of UGX: ${Number(pcnForm.remaining_balance || 0).toLocaleString()}` },
    ];
    statuses.forEach(s => {
      const checked = pcnForm.result_status === s.key;
      doc.setFillColor(checked ? 44 : 255, checked ? 62 : 255, checked ? 80 : 255);
      doc.rect(15, y - 3, 4, 4, checked ? 'F' : 'S');
      doc.setFont('helvetica', checked ? 'bold' : 'normal');
      doc.text(s.label, 22, y);
      if (s.key === 'partially_paid') {
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.text(`Amount in words: ${pcnForm.remaining_balance_words || ''}`, 22, y);
      }
      y += 7;
    });

    y += 2;
    doc.setFont('helvetica', 'normal');
    doc.text(`Remarks: ${pcnForm.remarks || ''}`, 15, y); y += 10;

    // Issued By
    if (officer?.signature_image) {
      try { const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG'; doc.addImage(officer.signature_image, fmt, 15, y, 38, 9); } catch(e) {}
    }
    y += 12;
    doc.setDrawColor(80,80,80); doc.line(15, y, 140, y);
    doc.setFontSize(8);
    doc.text('Issued By:', 15, y + 5);
    doc.text(`Name: ${officer ? (officer.full_name || officer.username) : ''}`, 15, y + 10);
    doc.text(`Position: ${officer ? (OFFICER_TITLES[officer.role] || officer.role) : ''}`, 15, y + 15);
    doc.text(`Signature: `, 15, y + 20);

    addFooter(doc);
    doc.save(`PCN-${memberData.member_number}-${ref}.pdf`);
  };

  // ── APPENDIX 10: CREDIT UTILISATION NOTIFICATION ───────────────────────
  const generateCUN = (officer) => {
    if (!memberData) return alert('Select a member first');
    const doc = new jsPDF();
    const ref = getRefNumber('CUN');
    let y = addKAHeader(doc, 'Credit Utilization Notification');

    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text(`Ref No: ${ref}`, 15, y + 4);
    doc.text(`Date: ${today()}`, 195, y + 4, { align: 'right' });
    y += 12;

    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text(`Member Name: ${memberData.full_name}`, 15, y); y += 6;
    doc.text(`Member ID/Number: ${memberData.member_number}`, 15, y); y += 8;

    doc.setFont('helvetica', 'bold');
    doc.text('Subject: Credit Adjustment Confirmation', 15, y); y += 8;
    doc.setFont('helvetica', 'normal');

    y = para(doc, y, `We acknowledge your previous overpayment of UGX ${Number(cunForm.credit_amount || 0).toLocaleString()} recorded under Credit Note No. ${cunForm.credit_note_ref} issued on: ${cunForm.credit_note_date || ''}.`, 15, 180);
    y += 2;
    y = para(doc, y, `We wish to confirm that this amount has been applied to offset your current obligation for: ${cunForm.obligation_desc || ''} dated ${cunForm.obligation_date || ''} for the amount of UGX ${Number(cunForm.obligation_amount || 0).toLocaleString()}.`, 15, 180);
    y += 4;

    doc.setFont('helvetica', 'bold');
    doc.text('Based on this adjustment, the following applies:', 15, y); y += 6;
    doc.setFont('helvetica', 'normal');

    const creditN = parseFloat(cunForm.credit_amount || 0);
    const obligN  = parseFloat(cunForm.obligation_amount || 0);
    const balN    = parseFloat(cunForm.balance_after || 0);

    const outcomes = [
      `* If your previous credit (UGX ${creditN.toLocaleString()}) is greater than the current obligation (UGX ${obligN.toLocaleString()},) your account remains with a positive balance of (UGX ${balN.toLocaleString()}) which will be carried forward.`,
      `* If your previous credit (UGX ${creditN.toLocaleString()}) is equal to the current obligation (UGX ${obligN.toLocaleString()}), your account is now fully settled with a balance of UGX 0.`,
      `* If your previous credit (UGX ${creditN.toLocaleString()}) is less than the current obligation (UGX ${obligN.toLocaleString()}), your account now reflects an outstanding balance of (UGX ${balN.toLocaleString()}) which is payable immediately.`,
    ];
    outcomes.forEach(o => { y = para(doc, y, o, 15, 180); y += 2; });
    y += 4;

    y = para(doc, y, 'Kindly treat this letter as confirmation of the adjustments made to your Member Individual Financial Ledger (MIFL). Please reach out to the Finance Committee for any clarification or updated statements.', 15, 180);
    y += 6;
    y = para(doc, y, 'Thank you for your continued support and commitment to the values of Kigezi Association.', 15, 180);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.text('Sincerely,', 15, y); y += 6;
    if (officer?.signature_image) {
      try { const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG'; doc.addImage(officer.signature_image, fmt, 15, y, 38, 9); } catch(e) {}
      y += 12;
    }
    doc.setFont('helvetica', 'normal');
    doc.text('Finance Committee', 15, y); y += 5;
    doc.text('Kigezi Association', 15, y);

    addFooter(doc);
    doc.save(`CUN-${memberData.member_number}-${ref}.pdf`);
  };

  // ── APPENDIX 11: OFFICIAL RECEIPT ───────────────────────────────────────
  const generateReceipt = (officer) => {
    if (!memberData) return alert('Select a member first');
    const doc = new jsPDF();
    const ref = receiptForm.receipt_no || getRefNumber('RCP');
    let y = addKAHeader(doc, 'OFFICIAL RECEIPT');

    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text(`Ref: ${ref}`, 15, y + 4);
    doc.text(`Date: ${today()}`, 195, y + 4, { align: 'right' });
    y += 12;

    y = addMemberBlock(doc, y, memberData);

    const pm = receiptForm.payment_method || 'Cash';
    const pmDisplay = pm === 'Bank Transfer' ? `Bank Transfer — ${receiptForm.bank_name || ''}` : pm;

    autoTable(doc, {
      startY: y,
      head: [['Field', 'Details']],
      body: [
        ['Amount Received (UGX)', Number(receiptForm.amount || 0).toLocaleString()],
        ['Amount in Words', receiptForm.amount_words || ''],
        ['Purpose of Payment', receiptForm.purpose || ''],
        ['Payment Method', pmDisplay],
        ['Date', today()],
      ],
      styles: { fontSize: 10 },
      headStyles: { fillColor: [44, 62, 80] },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
      margin: { left: 15, right: 15 },
    });
    y = doc.lastAutoTable.finalY + 10;

    doc.setFillColor(39, 174, 96); doc.rect(15, y, 180, 14, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
    doc.text('PAYMENT RECEIVED — THANK YOU', 105, y + 9, { align: 'center' });
    doc.setTextColor(0, 0, 0); y += 22;

    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text('This receipt is issued by the Finance Committee of Kigezi Association as proof of payment received.', 15, y, { maxWidth: 180 }); y += 14;

    if (officer?.signature_image) {
      try { const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG'; doc.addImage(officer.signature_image, fmt, 15, y, 38, 9); } catch(e) {}
    }
    y += 12;
    doc.setDrawColor(80,80,80);
    doc.line(15, y, 100, y); doc.line(110, y, 195, y);
    doc.setFontSize(8);
    doc.text('Received By (Finance Committee)', 15, y + 4);
    doc.text('Position', 110, y + 4);
    if (officer) {
      doc.setFont('helvetica', 'bold'); doc.text(officer.full_name || officer.username, 15, y + 9);
      doc.setFont('helvetica', 'normal'); doc.text(OFFICER_TITLES[officer.role] || '', 15, y + 13);
      doc.text(`Date: ${today()}`, 15, y + 17);
    }

    addFooter(doc);
    doc.save(`RECEIPT-${memberData.member_number}-${ref}.pdf`);
  };

  // ── APPENDIX 6: LOAN AGREEMENT ──────────────────────────────────────────
  const generateLoan = (officer) => {
    if (!memberData) return alert('Select a member first');
    const doc = new jsPDF();
    const ref = getRefNumber('LOAN');
    let y = addKAHeader(doc, 'LOAN AGREEMENT', '(Reserve and Stabilisation Fund Facility)');

    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text(`This Agreement is made pursuant to the authority granted under Article 22 of the Kigezi Association Constitution.`, 15, y, { maxWidth: 180 });
    y += 10;
    doc.text(`Agreement Date: .......... day of .........., 20${new Date().getFullYear().toString().slice(2)}`, 15, y); y += 8;

    doc.setFont('helvetica', 'bold'); doc.text('Parties:', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('Lender: Kigezi Association', 18, y); y += 5;
    doc.text(`Borrower: ${memberData.full_name}`, 18, y); y += 5;
    doc.text(`National ID: ${memberData.national_id || ''}`, 18, y); y += 10;

    // Clause 1
    doc.setFont('helvetica', 'bold'); doc.text('1. LOAN PRINCIPAL', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`· Total Loan Amount: UGX ${Number(loanForm.loan_amount || 0).toLocaleString()}`, 18, y); y += 10;

    // Clause 2
    doc.setFont('helvetica', 'bold'); doc.text('2. REPAYMENT OPTIONS', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('(Member must select and complete ONE option only)', 18, y); y += 7;

    // Option A
    const optA = loanForm.repayment_option === 'A';
    doc.setFillColor(optA ? 44 : 255, optA ? 62 : 255, optA ? 80 : 255);
    doc.rect(18, y - 3, 4, 4, optA ? 'F' : 'S');
    doc.setFont('helvetica', optA ? 'bold' : 'normal');
    doc.text('OPTION A: LUMP SUM PAYMENT AT TERM END', 25, y); y += 5;
    doc.setFont('helvetica', 'normal');
    if (optA) {
      doc.text(`· Loan Term: ${loanForm.term_months || ''} months`, 25, y); y += 5;
      doc.text(`· Final Repayment Date: ${loanForm.final_date || ''}`, 25, y); y += 5;
      doc.text(`· Total Development Contribution: UGX ${Number(loanForm.total_dev_contribution || 0).toLocaleString()}`, 25, y); y += 5;
      doc.text(`· Total Amount Due at Term End (Principal + Contribution): UGX ${Number(loanForm.total_due || 0).toLocaleString()}`, 25, y); y += 8;
    } else { y += 3; }

    // Option B
    const optB = loanForm.repayment_option === 'B';
    doc.setFillColor(optB ? 44 : 255, optB ? 62 : 255, optB ? 80 : 255);
    doc.rect(18, y - 3, 4, 4, optB ? 'F' : 'S');
    doc.setFont('helvetica', optB ? 'bold' : 'normal');
    doc.text('OPTION B: MONTHLY INSTALLMENT PLAN', 25, y); y += 5;
    doc.setFont('helvetica', 'normal');
    if (optB) {
      doc.text(`· Repayment Period: ${loanForm.term_months || ''} months`, 25, y); y += 5;
      doc.text('· The definitive schedule of payments is detailed in Schedule A attached to this Agreement.', 25, y, { maxWidth: 165 }); y += 7;
      doc.text(`· Due Date: The ${loanForm.due_day || ''} of each month.`, 25, y); y += 5;
      doc.text(`· Monthly Instalment (UGX): ${Number(loanForm.monthly_instalment || 0).toLocaleString()}`, 25, y); y += 5;
      doc.text(`· Total Development Contribution (UGX): ${Number(loanForm.total_dev_contribution || 0).toLocaleString()}`, 25, y); y += 5;
      doc.text(`· Final Repayment Date: ${loanForm.final_date || ''}`, 25, y); y += 8;
    } else { y += 3; }

    // Clause 3
    doc.setFont('helvetica', 'bold'); doc.text('3. SECURITY & RECOVERY MANDATE', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'The Borrower hereby irrevocably agrees that:', 15, 180); y += 2;
    y = para(doc, y, '3.1. Right of Set-Off & Lien: The Association shall have a lien and right of set-off against the Borrower\'s Savings, Share Capital, and any funds held by the Association for the Borrower, and any future lump-sum payouts from the Association ROSCA Savings scheme.', 18, 174); y += 2;
    y = para(doc, y, loanForm.security_external ? `3.2. Pledge of External Security: External assets pledged as security: ${loanForm.security_external}. Governed by a separate pledge agreement.` : '3.2. Pledge of External Security: Member shareholding — no external security.', 18, 174); y += 2;
    y = para(doc, y, '3.3. Recovery Costs: The Borrower is liable for all debt recovery costs incurred by the Association.', 18, 174); y += 6;

    // Clause 4
    doc.setFont('helvetica', 'bold'); doc.text('4. BORROWER\'S OBLIGATIONS', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'The Borrower shall:', 15, 180);
    y = para(doc, y, `· Make all payments on time as specified in the selected option${optB ? ' and Schedule A' : ''}.`, 18, 174); y += 2;
    y = para(doc, y, '· Maintain membership in good standing for the loan duration.', 18, 174); y += 6;

    // Clause 5
    doc.setFont('helvetica', 'bold'); doc.text('5. DEFAULT CONSEQUENCES', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'Failure to pay any amount on time constitutes a default. Upon default:', 15, 180);
    y = para(doc, y, '5.1. A Debit Note will be issued for the overdue amount and a default penalty as determined by the Association.', 18, 174); y += 2;
    y = para(doc, y, '5.2. The following consequences are triggered immediately:', 18, 174);
    y = para(doc, y, '· Immediate suspension of all membership rights.', 22, 170); y += 1;
    y = para(doc, y, '· The entire outstanding balance becomes due immediately.', 22, 170); y += 1;
    y = para(doc, y, '· Immediate invocation of the Security & Recovery Mandate in Clause 3.', 22, 170); y += 6;

    // Clause 6: Declaration
    doc.setFont('helvetica', 'bold'); doc.text('6. BORROWER\'S DECLARATION', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'I acknowledge receipt of the loan principal. I understand that the Development Contribution is a mandatory fee and not interest. I have read, understood, and irrevocably agree to all terms above.', 15, 180); y += 8;

    if (y > 230) { doc.addPage(); y = 20; }

    // Signatures
    const colW3 = 56;
    doc.setFontSize(8);
    ['Borrower\'s Signature', 'Finance Chairperson', 'Association Chairperson'].forEach((lbl, i) => {
      const sx = 15 + i * (colW3 + 4);
      if (i === 1 && officer?.signature_image) {
        try { const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG'; doc.addImage(officer.signature_image, fmt, sx, y, 38, 9); } catch(e) {}
      }
      doc.setDrawColor(80,80,80); doc.line(sx, y + 12, sx + colW3, y + 12);
      doc.text(lbl, sx, y + 17);
      if (i === 0) { doc.text(memberData.full_name, sx, y + 22); doc.text(`Date: ${today()}`, sx, y + 27); }
      else if (i === 1 && officer) { doc.setFont('helvetica', 'bold'); doc.text(officer.full_name || officer.username, sx, y + 22); doc.setFont('helvetica', 'normal'); doc.text(OFFICER_TITLES[officer.role] || '', sx, y + 27); doc.text(`Date: ${today()}`, sx, y + 32); }
      else { doc.line(sx, y + 22, sx + colW3, y + 22); doc.text('Date:', sx, y + 26); }
    });
    y += 38;

    // WITNESSED BY
    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text('WITNESSED BY:', 15, y); y += 6;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.setDrawColor(80,80,80);
    doc.line(18, y + 8, 88, y + 8); doc.text('Name', 18, y + 12);
    doc.line(95, y + 8, 155, y + 8); doc.text('Signature', 95, y + 12);
    doc.line(162, y + 8, 195, y + 8); doc.text('Date', 162, y + 12);
    y += 18;

    addFooter(doc);
    doc.save(`LOAN-${memberData.member_number}-${ref}.pdf`);
  };

  // ── APPENDIX 1: MEMBER BINDING AGREEMENT ───────────────────────────────
  const generateMBA = (officer) => {
    if (!memberData) return alert('Select a member first');
    const doc = new jsPDF();
    let y = addKAHeader(doc, 'MEMBER BINDING AGREEMENT');

    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    y = para(doc, y, `This agreement is made on .......... day of .........., 20${new Date().getFullYear().toString().slice(2)} between:`, 15, 180); y += 2;
    y = para(doc, y, 'Kigezi Association, a registered community-based organisation (hereinafter referred to as "the Association")', 15, 180); y += 2;
    doc.setFont('helvetica', 'bold'); doc.text('AND', 105, y, { align: 'center' }); y += 6;
    doc.setFont('helvetica', 'normal');
    doc.text(`Full Name of Member: ${memberData.full_name}`, 15, y); y += 6;
    doc.text(`National ID: ${mbaForm.national_id || ''}`, 15, y); y += 10;

    doc.setFont('helvetica', 'bold'); doc.text('BACKGROUND', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'a) This Agreement is made under the authority of and to give effect to Article (4) of the Kigezi Association Constitution (hereinafter "the Constitution").', 15, 180); y += 2;
    y = para(doc, y, 'b) The member acknowledges that they have received their own personal copy of the full Constitution, read and understood its contents, and have had the opportunity to seek independent legal advice on it before signing this agreement.', 15, 180); y += 8;

    const sections = [
      ['1. PURPOSE AND CONSTITUTIONAL AUTHORITY', 'This agreement confirms my acceptance and binding commitment to all financial, legislative, enforcement provisions and the universal member undertakings established in the Constitution.'],
      ['2. MEMBER IRREVOCABLE COMMITMENTS', 'I, the undersigned member, irrevocably agree and declare that:'],
    ];
    sections.forEach(([h, b]) => { doc.setFont('helvetica', 'bold'); doc.text(h, 15, y); y += 5; doc.setFont('helvetica', 'normal'); y = para(doc, y, b, 15, 180); y += 4; });

    const subsections = [
      ['2.1 Binding Nature of Financial Records', 'I acknowledge that all my unpaid contributions, fines, penalties, loan obligations and any other debts shall be recorded in my Member Individual Financial Ledger (MIFL) and traced through official Association fiscal control documents, namely: Member debit note, credit note, credit utilisation note, payment confirmation note.'],
      ['2.2 Acceptance of Tracking System as Evidence', 'I accept that the Association\'s official financial tracking system (MIFL) and the aforementioned documents shall constitute prima facie evidence of my debt and shall be admissible in any internal disciplinary proceeding or court of law.'],
      ['2.3 Undertaking to Repay', 'I undertake to pay all my contributions and clear all obligations by the dates set by the Association. I accept the application of all fines and penalties as provided in the Association Constitution.'],
      ['2.4 Pre-consent Recovery of All Obligations', 'I pre-emptively consent to the Association\'s recovery procedures for any default including but not limited to: The offsetting of my outstanding debts against my shares, savings or dividends in the Association. The suspension of my membership rights (including voting and loan access) as per the constitutional schedule. The initiation of legal action for debt recovery.'],
      ['2.5 Authority to Act for Loan Recovery', 'Specifically for loans, I agree that even in the absence of a separate loan agreement, the disbursement of loan funds as recorded in my member statement (internal transfer) creates a binding obligation to repay. For secured loans, I further agree that the Association is authorised to realise the collateral through its lawful process outlined in the Constitution and related security documents.'],
    ];
    subsections.forEach(([h, b]) => { if (y > 250) { doc.addPage(); y = 20; } doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(h, 18, y); y += 5; doc.setFont('helvetica', 'normal'); y = para(doc, y, b, 18, 175); y += 3; });

    if (y > 220) { doc.addPage(); y = 20; }
    const sections2 = [
      ['3. LEGAL ENFORCEMENT', [
        '3.1 This agreement, together with the Association Constitution, constitutes a binding contract under Uganda law.',
        '3.2 The Association has the right to recover any outstanding amounts from me through all available legal means and I will be liable for all associated recovery costs including legal fees.',
        '3.3 All official documents issued to me (debit notes, credit notes, payment confirmation, credit utilisation notes, and MIFL records) shall be admissible as evidence in any legal process of debt recovery.',
      ]],
      ['4. DURATION, TERMINATION AND FINAL SETTLEMENT', [
        '4.1 This Agreement shall remain in full force for the entire course of my membership.',
        '4.2 This Agreement remains legally binding and enforceable even beyond termination or expiry of my membership for any reason including resignation or expulsion, until all my obligations to the Association are fully settled.',
        '4.3 This Agreement shall only be invalidated upon my expulsion or termination after a final settlement is reached confirming that: a) I have no outstanding balance debt with the Association, and b) All my financial claims from the Association have been settled.',
      ]],
      ['5. INTERPRETATION AND DEFINITIONS', [
        'For the purpose of this Agreement, "Final Settlement" shall be determined by a clearance certificate issued by the Finance Chairperson and ratified by the Executive Committee, which shall be conclusive evidence thereof.',
      ]],
    ];
    sections2.forEach(([h, bullets]) => {
      if (y > 230) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(h, 15, y); y += 5;
      bullets.forEach(b => { doc.setFont('helvetica', 'normal'); y = para(doc, y, b, 18, 175); y += 2; });
      y += 3;
    });

    if (y > 200) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.text('SIGNATURES', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'I, the undersigned member, hereby confirm my understanding and acceptance of all the above terms, including the background and definitions, and reaffirm that I have received and read the Constitution.', 15, 180); y += 6;

    doc.text(`Member Name: ${memberData.full_name}`, 15, y); y += 8;
    doc.line(15, y, 100, y); doc.text('Signature', 15, y + 4); y += 12;
    doc.text(`Date: ${mbaForm.signed_date || today()}`, 15, y); y += 10;

    doc.setFont('helvetica', 'bold'); doc.text('For and on behalf of Kigezi Association', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    if (officer?.signature_image) { try { const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG'; doc.addImage(officer.signature_image, fmt, 15, y, 38, 9); } catch(e) {} }
    y += 12; doc.line(15, y, 100, y);
    doc.text(`Name: ${officer ? (officer.full_name || officer.username) : ''}`, 15, y + 4);
    doc.line(15, y + 9, 100, y + 9); doc.text('Signature:', 15, y + 13); doc.text(`Date: ${today()}`, 110, y + 13); y += 18;

    doc.setFont('helvetica', 'bold'); doc.text('Witnesses:', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    for (let i = 1; i <= 4; i++) { doc.line(22, y, 180, y); doc.text(`${i}.`, 15, y - 1); y += 8; }
    y += 4;

    doc.setFont('helvetica', 'bold'); doc.text('Witnessed by a commissioner for oaths/Advocate/Local Authority', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('I confirm that;', 15, y); y += 5;
    doc.text('1. The member signed this document in my presence.', 15, y); y += 5;
    doc.text('2. The member confirmed to me that they have received a copy of the Constitution and read it.', 15, y, { maxWidth: 180 }); y += 7;
    doc.text('3. The member appeared to understand the contents and signed voluntarily.', 15, y, { maxWidth: 180 }); y += 8;
    doc.line(15, y + 5, 70, y + 5); doc.text('Name', 15, y + 9); doc.line(75, y + 5, 130, y + 5); doc.text('Signature', 75, y + 9); doc.line(135, y + 5, 185, y + 5); doc.text('Date', 135, y + 9);

    addFooter(doc);
    doc.save(`MBA-${memberData.member_number}.pdf`);
  };

  // ── APPENDIX 2: LEADERSHIP CODE & INTEGRITY DECLARATION ─────────────────
  const generateLCC = (officer) => {
    if (!memberData) return alert('Select a member first');
    const doc = new jsPDF();
    let y = addKAHeader(doc, 'LEADERSHIP CODE & INTEGRITY DECLARATION AGREEMENT');

    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'This Agreement is made pursuant to the authority granted under Article 11 of the Kigezi Association Constitution.', 15, 180); y += 4;
    doc.text(`This Agreement is made on the ......... day of ........., 20${new Date().getFullYear().toString().slice(2)}.`, 15, y); y += 10;

    doc.setFont('helvetica', 'bold'); doc.text('1. PARTIES', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('1.1 The Kigezi Association — A duly registered Community-Based Organisation.', 18, y); y += 5;
    doc.setFont('helvetica', 'bold'); doc.text('1.2 The Leader', 18, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${memberData.full_name}`, 22, y); y += 5;
    doc.text(`Office/Position: ${lccForm.position_title}`, 22, y); y += 5;
    doc.text(`National ID: ${memberData.national_id || ''}`, 22, y); y += 5;
    doc.text(`Phone Contact: ${memberData.phone || ''}`, 22, y); y += 5;
    doc.text(`Election / Appointment Date: ${lccForm.election_date || ''}`, 22, y); y += 5;
    doc.text(`Term Start Date: ${lccForm.term_start || ''}`, 22, y); y += 5;
    doc.text(`Term End Date: ${lccForm.term_end || ''}`, 22, y); y += 8;

    doc.setFont('helvetica', 'bold'); doc.text('2. PURPOSE OF THIS AGREEMENT', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'This Agreement sets out the ethical, professional and legal obligations required of all leaders of the Association. By signing this form, the leader voluntarily accepts and commits to abide by the provisions of: The Kigezi Association Constitution, The Association\'s By-laws, Leadership Code of Conduct, All financial, administrative and disciplinary regulations. Failure to comply shall constitute grounds for disciplinary action.', 15, 180); y += 6;

    const commitments = [
      ['3.1 Integrity and Honesty', 'I shall conduct all Association business truthfully, fairly and without deception, manipulation or concealment.'],
      ['3.2 Accountability', 'I shall be fully accountable for my actions, decisions and stewardship of any funds, assets or responsibilities entrusted to me.'],
      ['3.3 Transparency', 'I shall disclose any information required for proper record-keeping, decision-making and audits.'],
      ['3.4 Conflict of Interest', 'I shall promptly declare any personal interest that may conflict with Association duties. I shall not use my position for personal gain.'],
      ['3.5 Confidentiality', 'I shall protect all confidential information received through my leadership role.'],
      ['3.6 Non-Abuse of Office', 'I shall not misuse my position to intimidate, influence, exploit or unfairly benefit from any member.'],
      ['3.7 Financial Integrity', 'I shall not misappropriate, misuse, divert, under-report or conceal any Association funds. I accept personal liability for any financial loss caused by my negligence or misconduct.'],
    ];
    doc.setFont('helvetica', 'bold'); doc.text('3. CORE INTEGRITY COMMITMENTS', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('I hereby commit to the following:', 15, y); y += 5;
    commitments.forEach(([h, b]) => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold'); doc.text(h, 18, y); y += 4;
      doc.setFont('helvetica', 'normal'); y = para(doc, y, b, 22, 170); y += 2;
    });

    if (y > 220) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.text('4. PROHIBITED CONDUCT', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('I shall not:', 15, y); y += 5;
    ['a) Engage in corruption, fraud, forgery, misrepresentation or bribery',
     'b) Misuse Association property or funds',
     'c) Engage in favoritism or discrimination',
     'd) Obstruct audits, investigations or financial reviews',
     'e) Conceal material information',
     'f) Act in a manner that brings the Association into disrepute',
    ].forEach(t => { doc.text(t, 18, y); y += 5; });
    y += 3;

    doc.setFont('helvetica', 'bold'); doc.text('5. DISCIPLINARY CONSENT', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'I acknowledge and accept that if I breach this Code: I may be suspended, removed from office or barred from future leadership roles. I may be required to refund or compensate the Association for financial loss. I may face disciplinary hearings as provided under the Constitution. The Association may pursue legal action where necessary. I waive any claim against the Association arising from disciplinary action taken in accordance with the Constitution and this Agreement.', 15, 180); y += 4;

    ['6. INDEMNITY|I agree to indemnify and hold the Association harmless against any loss, liability, damage, or legal claim arising from my misconduct, negligence or unauthorized actions while in leadership.',
     '7. TERM OF THE AGREEMENT|This Agreement takes effect immediately upon signing, remains valid for the full term of my leadership, and must be reaffirmed upon re-election or re-appointment.',
    ].forEach(s => {
      const [h, b] = s.split('|');
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold'); doc.text(h, 15, y); y += 5;
      doc.setFont('helvetica', 'normal'); y = para(doc, y, b, 15, 180); y += 4;
    });

    if (y > 200) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.text('8. DECLARATION BY THE LEADER', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'I hereby affirm that: I have read and understood this Leadership Code & Integrity Declaration. I accept to be bound by its provisions. I shall uphold the highest standards of conduct in service to the Association. All information I have provided is true and correct.', 15, 180); y += 6;

    if (officer?.signature_image) { try { const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG'; doc.addImage(officer.signature_image, fmt, 15, y, 38, 9); } catch(e) {} }
    y += 12; doc.line(15, y, 100, y);
    doc.setFontSize(8);
    doc.text(`Signature`, 15, y + 4);
    doc.text(`Full Name: ${memberData.full_name}`, 15, y + 9);
    doc.text(`Date: ${lccForm.agreement_date || today()}`, 15, y + 14); y += 20;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text('9. WITNESSES', 15, y); y += 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.text('Name                          Signature                    Contact', 15, y); y += 5;
    for (let i = 1; i <= 2; i++) { doc.line(22, y, 90, y); doc.text(`${i}.`, 15, y - 1); doc.line(95, y, 155, y); doc.line(160, y, 195, y); y += 8; }
    y += 4;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text('10. FOR OFFICIAL USE – KIGEZI ASSOCIATION', 15, y); y += 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.line(15, y + 5, 110, y + 5); doc.text('Received and recorded by:', 15, y + 9); doc.line(115, y + 5, 195, y + 5); doc.text('Position:', 115, y + 9); y += 14;
    doc.line(15, y + 5, 100, y + 5); doc.text('Signature:', 15, y + 9); doc.text(`Date: ${today()}`, 110, y + 9);

    addFooter(doc);
    doc.save(`LCC-${memberData.member_number}.pdf`);
  };

  // ── APPENDIX 3: SAVINGS SCHEME BINDING AGREEMENT ───────────────────────
  const generateSavingsAgreement = (officer) => {
    if (!memberData) return alert('Select a member first');
    const doc = new jsPDF();
    let y = addKAHeader(doc, 'SAVINGS SCHEME BINDING AGREEMENT');

    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'This Agreement is made pursuant to the authority granted under Article 20 of the Kigezi Association Constitution.', 15, 180); y += 4;
    doc.text(`This Agreement is made on .......... day of .........., 20${new Date().getFullYear().toString().slice(2)}`, 15, y); y += 6;
    doc.setFont('helvetica', 'bold'); doc.text('BETWEEN KIGEZI ASSOCIATION (hereinafter referred to as "the Association")', 15, y); y += 5;
    doc.setFont('helvetica', 'bold'); doc.text('AND', 105, y, { align: 'center' }); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`Member Name: ${memberData.full_name}`, 15, y); y += 5;
    doc.text(`National ID: ${memberData.national_id || ''}`, 15, y); y += 8;

    const articles = [
      ['ARTICLE 1: SCHEME DETAILS', [
        `· Cycle: ${savingsForm.cycle_ref || ''}`,
        `· Monthly Contribution: UGX ${Number(savingsForm.monthly_amount || 0).toLocaleString()}`,
        `· Due Date: ${savingsForm.due_day || ''} of each month`,
        `· Cycle Period: ${savingsForm.cycle_start || ''} to ${savingsForm.cycle_end || ''}`,
        `· Total Positions in Cycle: ${savingsForm.total_positions || ''}`,
        `· Member's Assigned Position: ${savingsForm.member_position || ''}`,
        '· Payout Sequence: Determined by random lottery',
        `· Expected Payout Date: ${savingsForm.expected_payout_date || ''}`,
      ]],
      ['ARTICLE 2: MEMBER\'S BINDING COMMITMENTS', [
        '2.1 Acceptance of Terms',
        'The Member hereby irrevocably accepts: The payout sequence outcome as determined. The monthly contribution amount. The expected payout date.',
        '2.2 Payment Obligation',
        'The Member shall pay all monthly contributions for the entire cycle duration, notwithstanding prior receipt of the lump sum payout.',
      ]],
      ['ARTICLE 3: ASSOCIATION\'S OBLIGATIONS', [
        '· Maintain accurate records',
        '· Disburse lump sum per the determined sequence',
        '· Provide regular financial updates',
      ]],
      ['ARTICLE 4: PAYOUT TERMS', [
        '4.1 Payment Method',
        `The lump sum payout shall be transferred to: Bank: ${savingsForm.bank_name || ''} Account Name: ${savingsForm.account_name || ''} Account Number: ${savingsForm.account_number || ''}`,
        '4.2 Proof of Payment',
        "The Association's transfer record showing transaction to the above account shall serve as conclusive evidence of payment receipt.",
      ]],
      ['ARTICLE 5: FINANCIAL CONSISTENCY REQUIREMENT', [
        '5.1 Contribution Consistency: The Member shall maintain consistent monthly contributions without interruption. Any pattern of irregular payments shall constitute grounds for payout postponement.',
        '5.2 Zero Balance Prerequisite: The Member must clear all outstanding balances with the Association before being eligible for lump sum disbursement.',
        '5.3 Disbursement Postponement: In the event of contribution inconsistencies or outstanding balances, the Association shall postpone the Member\'s payout until all monthly contributions are current and consistent, any outstanding balances are fully settled, and the Member demonstrates financial reliability.',
      ]],
      ['ARTICLE 6: DEFAULT CONSEQUENCES', [
        '· Late payments attract penalty fees',
        '· Default may result in payout postponement',
        '· Association may recover debts from member\'s other savings',
        '· Legal recovery proceedings may be initiated after exhausting all internal recovery procedures',
      ]],
      ['ARTICLE 7: DISPUTE RESOLUTION', [
        '· Internal grievance procedures must be exhausted first',
        '· Ugandan law shall govern this agreement',
      ]],
    ];

    articles.forEach(([title, lines]) => {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(title, 15, y); y += 5;
      lines.forEach(l => {
        doc.setFont('helvetica', 'normal');
        y = para(doc, y, l, 18, 174); y += 1;
      });
      y += 4;
    });

    if (y > 200) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.text('MEMBER\'S SOLEMN DECLARATION', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    const decls = [
      'I HEREBY CONFIRM THAT:',
      '1. I accept the payout sequence outcome without reservation',
      '2. I commit to pay all monthly contributions until cycle completion',
      '3. I have provided accurate bank/mobile money details for payout',
      '4. I understand contribution inconsistencies may postpone my payout',
      '5. I will maintain zero outstanding balances with the Association',
      '6. I understand and accept all terms of this binding agreement',
    ];
    decls.forEach(d => { doc.text(d, 15, y); y += 6; });
    y += 4;

    doc.text('SIGNED BY MEMBER:', 15, y); y += 4;
    doc.text(`Name: ${memberData.full_name}`, 15, y); y += 8;
    doc.line(15, y, 90, y); doc.text('Signature', 15, y + 4);
    doc.text(`Date: ${savingsForm.signed_date || today()}`, 100, y + 4); y += 12;

    doc.text('WITNESSED BY:', 15, y); y += 4;
    doc.line(15, y, 90, y); doc.text('Name', 15, y + 4);
    doc.line(100, y, 175, y); doc.text('Signature', 100, y + 4);
    doc.line(15, y + 9, 100, y + 9); doc.text('Date:', 15, y + 13); y += 18;

    doc.setFont('helvetica', 'bold'); doc.text('FOR KIGEZI ASSOCIATION:', 15, y); y += 4;
    doc.setFont('helvetica', 'normal');
    if (officer?.signature_image) { try { const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG'; doc.addImage(officer.signature_image, fmt, 15, y, 38, 9); } catch(e) {} }
    y += 12; doc.line(15, y, 90, y);
    doc.text(`Name: ${officer ? (officer.full_name || officer.username) : ''}`, 15, y + 4);
    doc.text(`Position: ${officer ? (OFFICER_TITLES[officer.role] || '') : ''}`, 15, y + 9);
    doc.text(`Date: ${today()}`, 15, y + 14);

    addFooter(doc);
    doc.save(`SSA-${memberData.member_number}.pdf`);
  };

  // ── APPENDIX 4: ROSCA SWAP AGREEMENT ────────────────────────────────────
  const generateROSCASwap = (officer) => {
    const doc = new jsPDF();
    let y = addKAHeader(doc, 'ROSCA PAYOUT POSITION EXCHANGE (SWAP) AGREEMENT');

    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'This Agreement is made pursuant to the authority granted under Article 19 of the Kigezi Association Constitution.', 15, 180); y += 4;
    doc.text(`This Agreement is made on the ......... day of ........., 20${new Date().getFullYear().toString().slice(2)}.`, 15, y); y += 8;

    doc.setFont('helvetica', 'bold'); doc.text('PARTIES', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('The Association: Kigezi Association (hereinafter referred to as "the Association").', 15, y); y += 6;

    doc.setFont('helvetica', 'bold'); doc.text('Member A (Original Earlier Position Holder)', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`Full Name: ${roscaSwapForm.member_a_name}`, 18, y); y += 5;
    doc.text(`National ID: ${roscaSwapForm.member_a_id || ''}`, 18, y); y += 5;
    doc.text(`Phone Contact: ${roscaSwapForm.member_a_phone || ''}`, 18, y); y += 7;

    doc.setFont('helvetica', 'bold'); doc.text('Member B (Original Later Position Holder)', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`Full Name: ${roscaSwapForm.member_b_name}`, 18, y); y += 5;
    doc.text(`National ID: ${roscaSwapForm.member_b_id || ''}`, 18, y); y += 5;
    doc.text(`Phone Contact: ${roscaSwapForm.member_b_phone || ''}`, 18, y); y += 8;

    // Article 1
    doc.setFont('helvetica', 'bold'); doc.text('ARTICLE 1: DETAILS OF THE EXCHANGE', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`1.1 ROSCA Cycle: ${roscaSwapForm.cycle_ref}`, 18, y); y += 5;
    doc.text('1.2 Original Payout Positions:', 18, y); y += 5;
    doc.text(`Member A: Position No. ${roscaSwapForm.member_a_position}`, 22, y); y += 5;
    doc.text(`Member B: Position No. ${roscaSwapForm.member_b_position}`, 22, y); y += 5;
    doc.text('1.3 New Payout Positions After Exchange:', 18, y); y += 5;
    doc.text(`Member A: Position No. ${roscaSwapForm.member_b_position} (formerly held by Member B)`, 22, y); y += 5;
    doc.text(`Member B: Position No. ${roscaSwapForm.member_a_position} (formerly held by Member A)`, 22, y); y += 5;
    doc.text(`1.4 Effective Date of Exchange: ${roscaSwapForm.swap_date || ''}`, 18, y); y += 8;

    const articles = [
      ['ARTICLE 2: MUTUAL CONSENT AND DISCLOSURE', [
        '2.1 Voluntary Agreement: Both members confirm that this exchange is voluntary and free from coercion, misrepresentation or undue influence from any party, including the Association.',
        '2.2 No Change in Obligations: The exchange: a) Does not alter the members\' monthly contribution requirements; b) Does not affect fines, arrears or balances already recorded in each member\'s MIFL; c) Does not create a right to compensation, refund or repayment between members or from the Association.',
        '2.3 Existing Arrears: Any arrears or penalties recorded in a member\'s ledger remain solely that member\'s responsibility. Swapping positions does not erase, reduce or transfer arrears.',
        '2.4 No Private Liability: The Association shall not be responsible for any private payments, favours or personal agreements made between the members as part of this swap.',
        '2.5 Full Disclosure Confirmation: Each member acknowledges they have fully understood: The value implications of exchanging an earlier vs later payout position. Their remaining obligations after the swap. That once approved, the swap is binding and cannot be reversed except through a new signed agreement.',
      ]],
      ['ARTICLE 3: ASSOCIATION RECOGNITION', [
        '3.1 Verification: Before approving the swap, the Association shall verify: a) The membership status of both parties; b) Each member\'s arrears standing; c) Compliance with ROSCA rules.',
        '3.2 Approval and Registration: Once verified, the Association will update the payout sequence register and recognize the new order for all administrative and financial purposes.',
        '3.3 Right to Decline: The Association reserves the right to decline or suspend recognition of the swap if: a) Either member has unresolved arrears or disciplinary issues; b) Fraud, coercion or forgery is suspected; c) The swap violates the Constitution, By-laws or ROSCA regulations.',
        '3.4 Indemnity: Both members jointly agree to indemnify and hold the Association harmless against any claims, disputes or losses arising from this exchange.',
      ]],
      ['ARTICLE 4: DISPUTE RESOLUTION AND GOVERNING LAW', [
        '4.1 All disputes must first be resolved through the Association\'s internal grievance mechanisms.',
        '4.2 No external legal action may be taken without exhausting internal processes.',
        '4.3 This Agreement shall be governed by the laws of the Republic of Uganda.',
      ]],
    ];
    articles.forEach(([h, lines]) => {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold'); doc.text(h, 15, y); y += 5;
      lines.forEach(l => { doc.setFont('helvetica', 'normal'); y = para(doc, y, l, 18, 174); y += 2; });
      y += 3;
    });

    if (y > 200) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.text('SIGNATURES', 15, y); y += 8;

    // Member A
    doc.setFont('helvetica', 'normal');
    doc.text('MEMBER A', 15, y); y += 4;
    doc.text('I confirm that I am voluntarily exchanging my payout position.', 15, y); y += 6;
    doc.line(15, y, 90, y);
    doc.text(`Signature`, 15, y + 4); doc.text(`Name: ${roscaSwapForm.member_a_name}`, 15, y + 9); doc.line(15, y + 14, 90, y + 14); doc.text('Date:', 15, y + 18); y += 24;

    doc.text('MEMBER B', 15, y); y += 4;
    doc.text('I confirm that I am voluntarily exchanging my payout position.', 15, y); y += 6;
    doc.line(15, y, 90, y);
    doc.text('Signature', 15, y + 4); doc.text(`Name: ${roscaSwapForm.member_b_name}`, 15, y + 9); doc.line(15, y + 14, 90, y + 14); doc.text('Date:', 15, y + 18); y += 24;

    doc.setFont('helvetica', 'bold'); doc.text('WITNESSES', 15, y); y += 4;
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text('Name                          National ID            Signature              Contact', 15, y); y += 5;
    for (let i = 1; i <= 2; i++) { doc.line(22, y, 70, y); doc.text(`${i}.`, 15, y-1); doc.line(75, y, 115, y); doc.line(120, y, 160, y); doc.line(165, y, 195, y); y += 8; }
    y += 4;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text('FOR KIGEZI ASSOCIATION OFFICIAL USE ONLY', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    if (officer?.signature_image) { try { const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG'; doc.addImage(officer.signature_image, fmt, 15, y, 38, 9); } catch(e) {} }
    y += 12;
    doc.text(`Verified by: ${officer ? (officer.full_name || officer.username) : ''}`, 15, y); y += 5;
    doc.text(`Designation: ${officer ? (OFFICER_TITLES[officer.role] || '') : ''}`, 15, y); y += 5;
    doc.line(15, y + 5, 100, y + 5); doc.text('Signature:', 15, y + 9); doc.text(`Date: ${today()}`, 110, y + 9);

    addFooter(doc);
    doc.save(`SWAP-${roscaSwapForm.cycle_ref || 'ROSCA'}.pdf`);
  };

  // ── APPENDIX 5: ROSCA TRANSFER AGREEMENT ────────────────────────────────
  const generateROSCATransfer = (officer) => {
    if (!memberData) return alert('Select transferor member');
    const doc = new jsPDF();
    let y = addKAHeader(doc, 'ROSCA POSITION TRANSFER & CONSENT AGREEMENT');

    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    y = para(doc, y, 'This Agreement is made pursuant to the authority granted under Article 19 of the Kigezi Association Constitution.', 15, 180); y += 4;
    doc.text(`This Agreement is made on the ......... day of ........., 20${new Date().getFullYear().toString().slice(2)}.`, 15, y); y += 8;

    doc.setFont('helvetica', 'bold'); doc.text('PARTIES', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text('1. The Association: Kigezi Association (hereinafter referred to as "the Association")', 15, y); y += 7;

    doc.setFont('helvetica', 'bold'); doc.text('2. Transferor (Outgoing Member)', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${memberData.full_name}`, 18, y); y += 5;
    doc.text(`National ID: ${memberData.national_id || ''}`, 18, y); y += 5;
    doc.text(`Phone Contact: ${memberData.phone || ''}`, 18, y); y += 7;

    doc.setFont('helvetica', 'bold'); doc.text('3. Transferee (Incoming Member)', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${roscaTransferForm.transferee_name}`, 18, y); y += 5;
    doc.text(`National ID: ${roscaTransferForm.transferee_id || ''}`, 18, y); y += 5;
    doc.text(`Phone Contact: ${roscaTransferForm.transferee_phone}`, 18, y); y += 8;

    doc.setFont('helvetica', 'bold'); doc.text('ARTICLE 1: TRANSFER DETAILS', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(`1.1 Cycle: ${roscaTransferForm.cycle_ref}`, 18, y); y += 5;
    doc.text(`1.2 Original Payout Position (Transferor): ${roscaTransferForm.position}`, 18, y); y += 5;
    doc.text(`1.3 New Payout Position Assigned to Transferee: ${roscaTransferForm.position}`, 18, y); y += 5;
    doc.text(`1.4 Effective Date of Transfer: ${roscaTransferForm.transfer_date || ''}`, 18, y); y += 5;
    doc.text(`1.5 Reason for Transfer: ${roscaTransferForm.reason || ''}`, 18, y); y += 8;

    const articles = [
      ['ARTICLE 2: RIGHTS AND OBLIGATIONS', [
        '2.1 Transfer of Rights: The Transferor hereby permanently relinquishes all rights, benefits and claims to the payout position described above.',
        `2.2 Acceptance of Obligations: The Transferee accepts full responsibility for the transferred position and agrees to: a) Pay the monthly contribution of UGX ${Number(roscaTransferForm.monthly_contribution || 0).toLocaleString()} b) Settle all penalties and obligations arising after the effective date c) Comply fully with the Constitution, By-laws and Savings Scheme Binding Agreement.`,
        '2.3 Existing Arrears: Any arrears, penalties or outstanding obligations recorded in the Transferor\'s MIFL remain the sole responsibility of the Transferor and are not transferred to the Transferee. The Association shall not approve this transfer unless the Transferor\'s arrears status has been verified.',
        '2.4 No Private Claims: The Association shall not be held liable for any private arrangements, side payments or personal compensations exchanged between the Transferor and Transferee.',
        '2.5 Indemnity: Both parties jointly and severally agree to indemnify and hold the Association harmless from any disputes, claims, losses or liabilities arising from this transfer.',
      ]],
      ['ARTICLE 3: ASSOCIATION ACKNOWLEDGMENT', [
        '3.1 Verification: The Association shall verify: a) Membership validity of the Transferee b) Arrears status of both parties c) Compliance with ROSCA rules.',
        '3.2 Approval: Upon approval, the Association shall update the official payout register and recognize the Transferee as the lawful holder of the position.',
        '3.3 Right to Reject or Suspend: The Association may reject, delay or reverse recognition of the transfer if: a) Either party has unresolved arrears or disciplinary matters b) The transfer violates the Constitution, By-laws or cycle rules c) Fraud, coercion, misrepresentation or forgery is suspected.',
      ]],
      ['ARTICLE 4: DISPUTE RESOLUTION', [
        '4.1 All disputes arising from this Agreement shall be handled first through the Association\'s internal dispute resolution mechanisms.',
        '4.2 Should escalation be required, this Agreement shall be governed by the laws of the Republic of Uganda.',
        '4.3 No party may institute external legal action without first exhausting internal procedures.',
      ]],
    ];
    articles.forEach(([h, lines]) => {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold'); doc.text(h, 15, y); y += 5;
      lines.forEach(l => { doc.setFont('helvetica', 'normal'); y = para(doc, y, l, 18, 174); y += 2; });
      y += 3;
    });

    if (y > 200) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.text('DECLARATION AND SIGNATURES', 15, y); y += 6;

    doc.setFont('helvetica', 'normal');
    doc.text('TRANSFEROR', 15, y); y += 4;
    doc.text('I hereby confirm that I am voluntarily transferring my payout position without coercion and that all information provided is true.', 15, y, { maxWidth: 180 }); y += 8;
    doc.line(15, y, 90, y);
    doc.text(`Signature`, 15, y + 4); doc.text(`Name: ${memberData.full_name}`, 15, y + 9); doc.line(15, y + 14, 90, y + 14); doc.text('Date:', 15, y + 18); y += 24;

    doc.text('TRANSFEREE', 15, y); y += 4;
    doc.text('I accept the transfer and agree to fulfill all obligations attached to the position.', 15, y); y += 8;
    doc.line(15, y, 90, y);
    doc.text('Signature', 15, y + 4); doc.text(`Name: ${roscaTransferForm.transferee_name}`, 15, y + 9); doc.line(15, y + 14, 90, y + 14); doc.text('Date:', 15, y + 18); y += 24;

    doc.setFont('helvetica', 'bold'); doc.text('WITNESSES', 15, y); y += 4;
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text('Name                          Signature              Contact', 15, y); y += 5;
    for (let i = 1; i <= 2; i++) { doc.line(22, y, 80, y); doc.text(`${i}.`, 15, y-1); doc.line(85, y, 140, y); doc.line(145, y, 195, y); y += 8; }
    y += 4;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text('FOR OFFICIAL USE - KIGEZI ASSOCIATION', 15, y); y += 5;
    doc.setFont('helvetica', 'normal');
    if (officer?.signature_image) { try { const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG'; doc.addImage(officer.signature_image, fmt, 15, y, 38, 9); } catch(e) {} }
    y += 12;
    doc.text(`Verified by: ${officer ? (officer.full_name || officer.username) : ''}`, 15, y); y += 5;
    doc.text(`Position: ${officer ? (OFFICER_TITLES[officer.role] || '') : ''}`, 15, y); y += 5;
    doc.line(15, y + 5, 100, y + 5); doc.text('Signature:', 15, y + 9); doc.text(`Date: ${today()}`, 110, y + 9);

    addFooter(doc);
    doc.save(`XFER-${memberData.member_number}.pdf`);
  };

  // ── CLEARANCE CERTIFICATE (Not in appendices but needed operationally) ───
  const generateClearance = (officer) => {
    if (!memberData) return alert('Select a member first');
    const doc = new jsPDF();
    let y = addKAHeader(doc, 'CLEARANCE CERTIFICATE');

    y = addMemberBlock(doc, y, memberData);

    doc.setFillColor(39, 174, 96); doc.rect(15, y, 180, 16, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(12); doc.setFont('helvetica', 'bold');
    doc.text('THIS MEMBER IS HEREBY DECLARED FINANCIALLY CLEAR', 105, y + 10, { align: 'center' });
    doc.setTextColor(0, 0, 0); y += 24;

    const reasonLabels = { resignation: 'Voluntary Resignation', expulsion: 'Expulsion by Executive Committee', relocation: 'Relocation — Unable to Continue Membership', other: 'Other Approved Reason' };
    autoTable(doc, {
      startY: y,
      body: [
        ['Reason for Exit', reasonLabels[clearanceForm.reason] || clearanceForm.reason],
        ['MIFL Balance at Exit (UGX)', '0.00 — Fully Settled'],
        ['Certificate Issued By', clearanceForm.issued_by],
        ['Ratified By', clearanceForm.ratified_by],
        ['Date of Issue', clearanceForm.issue_date || today()],
      ],
      styles: { fontSize: 10 }, columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }, margin: { left: 15, right: 15 },
    });
    y = doc.lastAutoTable.finalY + 10;

    const clauses = [
      'This Clearance Certificate certifies that the above-named member has fully settled all financial obligations to Kigezi Association, including ROSCA contributions, savings, subscriptions, loan repayments, fines, and any other outstanding balances.',
      'The MIFL balance of the above member at the date of this certificate is UGX 0.00 (zero). This certificate terminates all financial obligations of the member to the Association.',
      'This certificate is issued under the authority of the Finance Chairperson and ratified by the Executive Committee as per Article 5 of the Kigezi Association Constitution.',
    ];
    clauses.forEach(c => { y = para(doc, y, c, 15, 180); y += 4; });
    y += 8;
    y = addSignatureBlock3(doc, y, officer);
    addFooter(doc);
    doc.save(`CLR-${memberData.member_number}.pdf`);
  };

  const addMemberBlock = (doc, y, member) => {
    doc.setFillColor(240, 240, 240); doc.rect(15, y, 180, 22, 'F');
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
    doc.text('MEMBER DETAILS', 18, y + 6);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
    doc.text(`Name: ${member.full_name || member.name}`, 18, y + 13);
    doc.text(`Member No: ${member.member_number}`, 18, y + 19);
    doc.text(`Phone: ${member.phone || 'N/A'}`, 110, y + 13);
    doc.text(`Email: ${member.email || 'N/A'}`, 110, y + 19);
    return y + 28;
  };

  const addSignatureBlock3 = (doc, y, officer) => {
    const colW = 56;
    ['Finance Chairperson (Issuing)', 'Executive Committee Ratification', 'Secretary General'].forEach((lbl, i) => {
      const sx = 15 + i * (colW + 4);
      if (i === 0 && officer?.signature_image) { try { const fmt = officer.signature_image.includes('data:image/png') ? 'PNG' : 'JPEG'; doc.addImage(officer.signature_image, fmt, sx, y, 38, 9); } catch(e) {} }
      doc.setDrawColor(80,80,80); doc.line(sx, y + 12, sx + colW, y + 12);
      doc.setFontSize(8); doc.text(lbl, sx, y + 17);
      if (i === 0 && officer) { doc.setFont('helvetica','bold'); doc.text(officer.full_name || officer.username, sx, y + 22); doc.setFont('helvetica','normal'); doc.text(`Date: ${today()}`, sx, y + 27); }
      else { doc.line(sx, y + 22, sx + colW, y + 22); doc.text('Date:', sx, y + 26); }
    });
    return y + 32;
  };

  // ── RENDER ───────────────────────────────────────────────────────────────
  const inputStyle = { width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px', color: '#2c3e50' };
  const fieldStyle = { marginBottom: '12px' };
  const btnStyle   = { background: '#2c3e50', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' };

  const docs = [
    { id: 'mdn',            label: 'Member Debit Note' },
    { id: 'pcn',            label: 'Payment Confirmation Note' },
    { id: 'mcn',            label: 'Member Credit Note' },
    { id: 'cun',            label: 'Credit Utilisation Notice' },
    { id: 'receipt',        label: 'Official Receipt' },
    { id: 'mba',            label: 'Member Binding Agreement' },
    { id: 'loan',           label: 'Loan Agreement' },
    { id: 'clearance',      label: 'Clearance Certificate' },
    { id: 'rosca_swap',     label: 'ROSCA Swap Agreement' },
    { id: 'rosca_transfer', label: 'ROSCA Transfer Agreement' },
    { id: 'savings',        label: 'Savings Scheme Agreement' },
    { id: 'lcc',            label: 'Leadership Code of Conduct' },
  ];

  const needsMember = activeDoc !== 'rosca_swap';

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      <h2 style={{ color: '#2c3e50', marginBottom: '6px' }}>Document Generator</h2>
      <p style={{ color: '#666', marginBottom: '24px', fontSize: '14px' }}>Generate official Kigezi Association documents as downloadable PDFs — templates match the Association Constitution exactly.</p>

      {/* Officer Modal */}
      {showOfficerModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '28px', width: '440px', maxWidth: '95vw', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 6px', color: '#2c3e50' }}>✍ Select Authorising Officer</h3>
            <p style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '18px' }}>The selected officer's name and signature will be printed on the document.</p>
            <div style={{ marginBottom: '16px' }}>
              {officers.length === 0 ? (
                <div style={{ color: '#e74c3c', fontSize: '13px' }}>No officer accounts found.</div>
              ) : (
                officers.map(o => (
                  <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', marginBottom: '8px', border: `2px solid ${String(selectedOfficerId) === String(o.id) ? '#2c3e50' : '#ecf0f1'}`, borderRadius: '8px', cursor: 'pointer', background: String(selectedOfficerId) === String(o.id) ? '#f0f4f8' : 'white' }}>
                    <input type="radio" name="officer" value={o.id} checked={String(selectedOfficerId) === String(o.id)} onChange={() => setSelectedOfficerId(String(o.id))} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#2c3e50' }}>{o.full_name || o.username}</div>
                      <div style={{ fontSize: '11px', color: '#7f8c8d' }}>{OFFICER_TITLES[o.role]}</div>
                    </div>
                    {o.signature_image ? (
                      <div style={{ background: '#f9f9f9', border: '1px solid #ddd', borderRadius: '4px', padding: '4px' }}>
                        <img src={o.signature_image} alt="sig" style={{ height: '28px', maxWidth: '80px', objectFit: 'contain' }} />
                      </div>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#e74c3c', background: '#fde8e8', padding: '2px 6px', borderRadius: '4px' }}>No signature</span>
                    )}
                  </label>
                ))
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={confirmAndGenerate} disabled={!selectedOfficerId} style={{ background: selectedOfficerId ? '#27ae60' : '#95a5a6', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: selectedOfficerId ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 'bold' }}>✓ Generate Document</button>
              <button onClick={skipOfficer} style={{ background: '#ecf0f1', color: '#2c3e50', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Skip</button>
              <button onClick={() => setShowOfficerModal(false)} style={{ background: '#fde8e8', color: '#c0392b', border: 'none', padding: '10px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '24px' }}>
        {docs.map(d => (
          <button key={d.id} onClick={() => setActiveDoc(d.id)}
            style={{ padding: '7px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '500', background: activeDoc === d.id ? '#2c3e50' : '#ecf0f1', color: activeDoc === d.id ? 'white' : '#2c3e50' }}>
            {d.label}
          </button>
        ))}
      </div>

      {/* Member Selector */}
      {needsMember && (
        <div style={{ ...fieldStyle, background: '#f8f9fa', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
          <label style={labelStyle}>Select Member</label>
          <select value={selectedMember} onChange={e => setSelectedMember(e.target.value)} style={inputStyle}>
            <option value=''>-- Select Member --</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.full_name || m.name} — {m.member_number}</option>)}
          </select>
          {memberData && <div style={{ marginTop: '8px', fontSize: '13px', color: '#27ae60' }}>✓ {memberData.full_name} | {memberData.member_number}</div>}
        </div>
      )}

      {/* Form Panel */}
      <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>

        {activeDoc === 'mdn' && (
          <div>
            <h3 style={{ color: '#c0392b', marginTop: 0 }}>Member Debit Note (MDN) — Appendix 7</h3>
            <div style={fieldStyle}><label style={labelStyle}>Nature of Obligation</label><input style={inputStyle} value={mdnForm.obligation_type} onChange={e => setMdnForm({...mdnForm, obligation_type: e.target.value})} placeholder="e.g. ROSCA Contribution — March 2026" /></div>
            <div style={fieldStyle}><label style={labelStyle}>Due Date Missed</label><input type="date" style={inputStyle} value={mdnForm.due_date_missed} onChange={e => setMdnForm({...mdnForm, due_date_missed: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Current Debit Amount (UGX)</label><input type="number" style={inputStyle} value={mdnForm.amount} onChange={e => setMdnForm({...mdnForm, amount: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Previous Outstanding Balance (UGX)</label><input type="number" style={inputStyle} value={mdnForm.previous_balance} onChange={e => setMdnForm({...mdnForm, previous_balance: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Notes / Remarks</label><textarea style={{...inputStyle, height: '60px'}} value={mdnForm.notes} onChange={e => setMdnForm({...mdnForm, notes: e.target.value})} /></div>
            <button style={btnStyle} onClick={() => openOfficerModal(generateMDN)}>Generate MDN PDF</button>
          </div>
        )}

        {activeDoc === 'pcn' && (
          <div>
            <h3 style={{ color: '#27ae60', marginTop: 0 }}>Payment Confirmation Note (PCN) — Appendix 9</h3>
            <div style={fieldStyle}><label style={labelStyle}>Previous Outstanding Balance (UGX)</label><input type="number" style={inputStyle} value={pcnForm.previous_balance} onChange={e => setPcnForm({...pcnForm, previous_balance: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Previous Balance in Words</label><input style={inputStyle} value={pcnForm.previous_balance_words} onChange={e => setPcnForm({...pcnForm, previous_balance_words: e.target.value})} placeholder="e.g. One Million Shillings Only" /></div>
            <div style={fieldStyle}><label style={labelStyle}>Amount Paid (UGX)</label><input type="number" style={inputStyle} value={pcnForm.amount_paid} onChange={e => setPcnForm({...pcnForm, amount_paid: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Amount Paid in Words</label><input style={inputStyle} value={pcnForm.amount_paid_words} onChange={e => setPcnForm({...pcnForm, amount_paid_words: e.target.value})} placeholder="e.g. One Million Shillings Only" /></div>
            <div style={fieldStyle}><label style={labelStyle}>Receipt No.</label><input style={inputStyle} value={pcnForm.receipt_no} onChange={e => setPcnForm({...pcnForm, receipt_no: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Date of Payment</label><input type="date" style={inputStyle} value={pcnForm.payment_date} onChange={e => setPcnForm({...pcnForm, payment_date: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Updated Account Status</label><select style={inputStyle} value={pcnForm.result_status} onChange={e => setPcnForm({...pcnForm, result_status: e.target.value})}><option value="cleared">Cleared — Zero balance</option><option value="overpaid">Overpaid — Positive balance</option><option value="partially_paid">Partially Paid — Balance still owed</option></select></div>
            {pcnForm.result_status === 'partially_paid' && <>
              <div style={fieldStyle}><label style={labelStyle}>Remaining Balance (UGX)</label><input type="number" style={inputStyle} value={pcnForm.remaining_balance} onChange={e => setPcnForm({...pcnForm, remaining_balance: e.target.value})} /></div>
              <div style={fieldStyle}><label style={labelStyle}>Remaining Balance in Words</label><input style={inputStyle} value={pcnForm.remaining_balance_words} onChange={e => setPcnForm({...pcnForm, remaining_balance_words: e.target.value})} /></div>
            </>}
            <div style={fieldStyle}><label style={labelStyle}>Remarks</label><input style={inputStyle} value={pcnForm.remarks} onChange={e => setPcnForm({...pcnForm, remarks: e.target.value})} /></div>
            <button style={btnStyle} onClick={() => openOfficerModal(generatePCN)}>Generate PCN PDF</button>
          </div>
        )}

        {activeDoc === 'mcn' && (
          <div>
            <h3 style={{ color: '#2980b9', marginTop: 0 }}>Member Credit Note (MCN) — Appendix 8</h3>
            <div style={fieldStyle}><label style={labelStyle}>Credit Note No. (optional — auto-generated if blank)</label><input style={inputStyle} value={mcnForm.credit_note_no} onChange={e => setMcnForm({...mcnForm, credit_note_no: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Amount Credited (UGX)</label><input type="number" style={inputStyle} value={mcnForm.amount} onChange={e => setMcnForm({...mcnForm, amount: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Amount in Words</label><input style={inputStyle} value={mcnForm.amount_words} onChange={e => setMcnForm({...mcnForm, amount_words: e.target.value})} placeholder="e.g. Two Million Shillings Only" /></div>
            <div style={fieldStyle}><label style={labelStyle}>Credit Type</label><select style={inputStyle} value={mcnForm.credit_type} onChange={e => setMcnForm({...mcnForm, credit_type: e.target.value})}><option>Prepayment</option><option>Overpayment</option></select></div>
            <div style={fieldStyle}><label style={labelStyle}>Payment Method</label><select style={inputStyle} value={mcnForm.payment_method} onChange={e => setMcnForm({...mcnForm, payment_method: e.target.value})}><option>Cash</option><option>Bank</option><option>Mobile Money</option></select></div>
            <div style={fieldStyle}><label style={labelStyle}>Balance After This Credit (UGX)</label><input type="number" style={inputStyle} value={mcnForm.balance_after} onChange={e => setMcnForm({...mcnForm, balance_after: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Balance After in Words</label><input style={inputStyle} value={mcnForm.balance_after_words} onChange={e => setMcnForm({...mcnForm, balance_after_words: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Remarks</label><input style={inputStyle} value={mcnForm.remarks} onChange={e => setMcnForm({...mcnForm, remarks: e.target.value})} /></div>
            <button style={btnStyle} onClick={() => openOfficerModal(generateMCN)}>Generate MCN PDF</button>
          </div>
        )}

        {activeDoc === 'cun' && (
          <div>
            <h3 style={{ color: '#8e44ad', marginTop: 0 }}>Credit Utilisation Notification (CUN) — Appendix 10</h3>
            <div style={fieldStyle}><label style={labelStyle}>Credit Note Reference No.</label><input style={inputStyle} value={cunForm.credit_note_ref} onChange={e => setCunForm({...cunForm, credit_note_ref: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Credit Note Issued On (date)</label><input type="date" style={inputStyle} value={cunForm.credit_note_date} onChange={e => setCunForm({...cunForm, credit_note_date: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Credit Amount (UGX)</label><input type="number" style={inputStyle} value={cunForm.credit_amount} onChange={e => setCunForm({...cunForm, credit_amount: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Obligation Description</label><input style={inputStyle} value={cunForm.obligation_desc} onChange={e => setCunForm({...cunForm, obligation_desc: e.target.value})} placeholder="e.g. ROSCA Contribution — April 2026" /></div>
            <div style={fieldStyle}><label style={labelStyle}>Obligation Date</label><input type="date" style={inputStyle} value={cunForm.obligation_date} onChange={e => setCunForm({...cunForm, obligation_date: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Obligation Amount (UGX)</label><input type="number" style={inputStyle} value={cunForm.obligation_amount} onChange={e => setCunForm({...cunForm, obligation_amount: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Resulting Balance After Adjustment (UGX)</label><input type="number" style={inputStyle} value={cunForm.balance_after} onChange={e => setCunForm({...cunForm, balance_after: e.target.value})} /></div>
            <button style={btnStyle} onClick={() => openOfficerModal(generateCUN)}>Generate CUN PDF</button>
          </div>
        )}

        {activeDoc === 'receipt' && (
          <div>
            <h3 style={{ color: '#27ae60', marginTop: 0 }}>Official Receipt — Appendix 11</h3>
            <div style={fieldStyle}><label style={labelStyle}>Receipt No. (optional — auto-generated if blank)</label><input style={inputStyle} value={receiptForm.receipt_no} onChange={e => setReceiptForm({...receiptForm, receipt_no: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Amount Received (UGX)</label><input type="number" style={inputStyle} value={receiptForm.amount} onChange={e => setReceiptForm({...receiptForm, amount: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>The sum of shillings (in words)</label><input style={inputStyle} value={receiptForm.amount_words} onChange={e => setReceiptForm({...receiptForm, amount_words: e.target.value})} placeholder="e.g. Two Million Shillings Only" /></div>
            <div style={fieldStyle}><label style={labelStyle}>Being payment of</label><input style={inputStyle} value={receiptForm.purpose} onChange={e => setReceiptForm({...receiptForm, purpose: e.target.value})} placeholder="e.g. ROSCA Contribution — April 2026" /></div>
            <div style={fieldStyle}><label style={labelStyle}>Payment Method</label><select style={inputStyle} value={receiptForm.payment_method} onChange={e => setReceiptForm({...receiptForm, payment_method: e.target.value})}><option>Cash</option><option>Mobile Money</option><option>Bank Transfer</option></select></div>
            {receiptForm.payment_method === 'Bank Transfer' && <div style={fieldStyle}><label style={labelStyle}>Bank Name</label><input style={inputStyle} value={receiptForm.bank_name || ''} onChange={e => setReceiptForm({...receiptForm, bank_name: e.target.value})} placeholder="e.g. Centenary Bank" /></div>}
            <button style={btnStyle} onClick={() => openOfficerModal(generateReceipt)}>Generate Receipt PDF</button>
          </div>
        )}

        {activeDoc === 'mba' && (
          <div>
            <h3 style={{ color: '#2c3e50', marginTop: 0 }}>Member Binding Agreement — Appendix 1</h3>
            <div style={fieldStyle}><label style={labelStyle}>National ID Number</label><input style={inputStyle} value={mbaForm.national_id} onChange={e => setMbaForm({...mbaForm, national_id: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Date of Signing</label><input type="date" style={inputStyle} value={mbaForm.signed_date} onChange={e => setMbaForm({...mbaForm, signed_date: e.target.value})} /></div>
            <button style={btnStyle} onClick={() => openOfficerModal(generateMBA)}>Generate MBA PDF</button>
          </div>
        )}

        {activeDoc === 'loan' && (
          <div>
            <h3 style={{ color: '#e67e22', marginTop: 0 }}>Loan Agreement — Appendix 6</h3>
            <div style={fieldStyle}><label style={labelStyle}>Total Loan Amount (UGX)</label><input type="number" style={inputStyle} value={loanForm.loan_amount} onChange={e => setLoanForm({...loanForm, loan_amount: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Repayment Option</label><select style={inputStyle} value={loanForm.repayment_option} onChange={e => setLoanForm({...loanForm, repayment_option: e.target.value})}><option value="A">Option A — Lump Sum at Term End</option><option value="B">Option B — Monthly Installment Plan</option></select></div>
            <div style={fieldStyle}><label style={labelStyle}>Repayment Term (Months)</label><input type="number" style={inputStyle} value={loanForm.term_months} onChange={e => setLoanForm({...loanForm, term_months: e.target.value})} /></div>
            {loanForm.repayment_option === 'A' && <>
              <div style={fieldStyle}><label style={labelStyle}>Total Development Contribution (UGX)</label><input type="number" style={inputStyle} value={loanForm.total_dev_contribution} onChange={e => setLoanForm({...loanForm, total_dev_contribution: e.target.value})} /></div>
              <div style={fieldStyle}><label style={labelStyle}>Total Amount Due at Term End (UGX)</label><input type="number" style={inputStyle} value={loanForm.total_due} onChange={e => setLoanForm({...loanForm, total_due: e.target.value})} /></div>
              <div style={fieldStyle}><label style={labelStyle}>Final Repayment Date</label><input type="date" style={inputStyle} value={loanForm.final_date} onChange={e => setLoanForm({...loanForm, final_date: e.target.value})} /></div>
            </>}
            {loanForm.repayment_option === 'B' && <>
              <div style={fieldStyle}><label style={labelStyle}>Due Day of Month (e.g. 15th)</label><input style={inputStyle} value={loanForm.due_day} onChange={e => setLoanForm({...loanForm, due_day: e.target.value})} placeholder="15th" /></div>
              <div style={fieldStyle}><label style={labelStyle}>Monthly Instalment (UGX)</label><input type="number" style={inputStyle} value={loanForm.monthly_instalment} onChange={e => setLoanForm({...loanForm, monthly_instalment: e.target.value})} /></div>
              <div style={fieldStyle}><label style={labelStyle}>Total Development Contribution (UGX)</label><input type="number" style={inputStyle} value={loanForm.total_dev_contribution} onChange={e => setLoanForm({...loanForm, total_dev_contribution: e.target.value})} /></div>
              <div style={fieldStyle}><label style={labelStyle}>Final Repayment Date</label><input type="date" style={inputStyle} value={loanForm.final_date} onChange={e => setLoanForm({...loanForm, final_date: e.target.value})} /></div>
            </>}
            <div style={fieldStyle}><label style={labelStyle}>Disbursement Date</label><input type="date" style={inputStyle} value={loanForm.disbursement_date} onChange={e => setLoanForm({...loanForm, disbursement_date: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>External Security (if any — leave blank for member shares only)</label><input style={inputStyle} value={loanForm.security_external} onChange={e => setLoanForm({...loanForm, security_external: e.target.value})} placeholder="e.g. Land Title LRV 1234" /></div>
            <button style={btnStyle} onClick={() => openOfficerModal(generateLoan)}>Generate Loan Agreement PDF</button>
          </div>
        )}

        {activeDoc === 'clearance' && (
          <div>
            <h3 style={{ color: '#27ae60', marginTop: 0 }}>Clearance Certificate</h3>
            <div style={fieldStyle}><label style={labelStyle}>Reason for Exit</label><select style={inputStyle} value={clearanceForm.reason} onChange={e => setClearanceForm({...clearanceForm, reason: e.target.value})}><option value="resignation">Voluntary Resignation</option><option value="expulsion">Expulsion</option><option value="relocation">Relocation</option><option value="other">Other</option></select></div>
            <div style={fieldStyle}><label style={labelStyle}>Issued By (Finance Chairperson name)</label><input style={inputStyle} value={clearanceForm.issued_by} onChange={e => setClearanceForm({...clearanceForm, issued_by: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Ratified By (Executive Committee)</label><input style={inputStyle} value={clearanceForm.ratified_by} onChange={e => setClearanceForm({...clearanceForm, ratified_by: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Date of Issue</label><input type="date" style={inputStyle} value={clearanceForm.issue_date} onChange={e => setClearanceForm({...clearanceForm, issue_date: e.target.value})} /></div>
            <button style={btnStyle} onClick={() => openOfficerModal(generateClearance)}>Generate Clearance Certificate PDF</button>
          </div>
        )}

        {activeDoc === 'rosca_swap' && (
          <div>
            <h3 style={{ color: '#16a085', marginTop: 0 }}>ROSCA Position Swap Agreement — Appendix 4</h3>
            <p style={{ fontSize: '13px', color: '#666' }}>Enter both members manually. Both must have zero arrears before signing.</p>
            <div style={fieldStyle}><label style={labelStyle}>Member A — Full Name</label><input style={inputStyle} value={roscaSwapForm.member_a_name} onChange={e => setRoscaSwapForm({...roscaSwapForm, member_a_name: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Member A — National ID</label><input style={inputStyle} value={roscaSwapForm.member_a_id} onChange={e => setRoscaSwapForm({...roscaSwapForm, member_a_id: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Member A — Phone</label><input style={inputStyle} value={roscaSwapForm.member_a_phone} onChange={e => setRoscaSwapForm({...roscaSwapForm, member_a_phone: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Member A — Current Position No.</label><input style={inputStyle} value={roscaSwapForm.member_a_position} onChange={e => setRoscaSwapForm({...roscaSwapForm, member_a_position: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Member B — Full Name</label><input style={inputStyle} value={roscaSwapForm.member_b_name} onChange={e => setRoscaSwapForm({...roscaSwapForm, member_b_name: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Member B — National ID</label><input style={inputStyle} value={roscaSwapForm.member_b_id} onChange={e => setRoscaSwapForm({...roscaSwapForm, member_b_id: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Member B — Phone</label><input style={inputStyle} value={roscaSwapForm.member_b_phone} onChange={e => setRoscaSwapForm({...roscaSwapForm, member_b_phone: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Member B — Current Position No.</label><input style={inputStyle} value={roscaSwapForm.member_b_position} onChange={e => setRoscaSwapForm({...roscaSwapForm, member_b_position: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>ROSCA Cycle Reference</label><input style={inputStyle} value={roscaSwapForm.cycle_ref} onChange={e => setRoscaSwapForm({...roscaSwapForm, cycle_ref: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Swap Date</label><input type="date" style={inputStyle} value={roscaSwapForm.swap_date} onChange={e => setRoscaSwapForm({...roscaSwapForm, swap_date: e.target.value})} /></div>
            <button style={btnStyle} onClick={() => openOfficerModal(generateROSCASwap)}>Generate Swap Agreement PDF</button>
          </div>
        )}

        {activeDoc === 'rosca_transfer' && (
          <div>
            <h3 style={{ color: '#d35400', marginTop: 0 }}>ROSCA Position Transfer Agreement — Appendix 5</h3>
            <p style={{ fontSize: '13px', color: '#666' }}>Select the Transferor (existing member) above. Enter Transferee details below.</p>
            <div style={fieldStyle}><label style={labelStyle}>Transferee Full Name</label><input style={inputStyle} value={roscaTransferForm.transferee_name} onChange={e => setRoscaTransferForm({...roscaTransferForm, transferee_name: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Transferee National ID</label><input style={inputStyle} value={roscaTransferForm.transferee_id} onChange={e => setRoscaTransferForm({...roscaTransferForm, transferee_id: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Transferee Phone</label><input style={inputStyle} value={roscaTransferForm.transferee_phone} onChange={e => setRoscaTransferForm({...roscaTransferForm, transferee_phone: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Position Being Transferred</label><input style={inputStyle} value={roscaTransferForm.position} onChange={e => setRoscaTransferForm({...roscaTransferForm, position: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Monthly Contribution Amount (UGX)</label><input type="number" style={inputStyle} value={roscaTransferForm.monthly_contribution} onChange={e => setRoscaTransferForm({...roscaTransferForm, monthly_contribution: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>ROSCA Cycle Reference</label><input style={inputStyle} value={roscaTransferForm.cycle_ref} onChange={e => setRoscaTransferForm({...roscaTransferForm, cycle_ref: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Transfer Date</label><input type="date" style={inputStyle} value={roscaTransferForm.transfer_date} onChange={e => setRoscaTransferForm({...roscaTransferForm, transfer_date: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Reason for Transfer</label><input style={inputStyle} value={roscaTransferForm.reason} onChange={e => setRoscaTransferForm({...roscaTransferForm, reason: e.target.value})} /></div>
            <button style={btnStyle} onClick={() => openOfficerModal(generateROSCATransfer)}>Generate Transfer Agreement PDF</button>
          </div>
        )}

        {activeDoc === 'savings' && (
          <div>
            <h3 style={{ color: '#2c3e50', marginTop: 0 }}>Savings Scheme Binding Agreement — Appendix 3</h3>
            <div style={fieldStyle}><label style={labelStyle}>ROSCA Cycle Reference</label><input style={inputStyle} value={savingsForm.cycle_ref} onChange={e => setSavingsForm({...savingsForm, cycle_ref: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Cycle Start Date</label><input type="date" style={inputStyle} value={savingsForm.cycle_start} onChange={e => setSavingsForm({...savingsForm, cycle_start: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Cycle End Date</label><input type="date" style={inputStyle} value={savingsForm.cycle_end} onChange={e => setSavingsForm({...savingsForm, cycle_end: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Monthly Contribution (UGX)</label><input type="number" style={inputStyle} value={savingsForm.monthly_amount} onChange={e => setSavingsForm({...savingsForm, monthly_amount: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Due Date (day of month)</label><input style={inputStyle} value={savingsForm.due_day} onChange={e => setSavingsForm({...savingsForm, due_day: e.target.value})} placeholder="e.g. 15th" /></div>
            <div style={fieldStyle}><label style={labelStyle}>Total Positions in Cycle</label><input type="number" style={inputStyle} value={savingsForm.total_positions} onChange={e => setSavingsForm({...savingsForm, total_positions: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Member's Assigned Position Number</label><input style={inputStyle} value={savingsForm.member_position} onChange={e => setSavingsForm({...savingsForm, member_position: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Expected Payout Date</label><input type="date" style={inputStyle} value={savingsForm.expected_payout_date} onChange={e => setSavingsForm({...savingsForm, expected_payout_date: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Bank Name</label><input style={inputStyle} value={savingsForm.bank_name} onChange={e => setSavingsForm({...savingsForm, bank_name: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Account Name</label><input style={inputStyle} value={savingsForm.account_name} onChange={e => setSavingsForm({...savingsForm, account_name: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Account Number</label><input style={inputStyle} value={savingsForm.account_number} onChange={e => setSavingsForm({...savingsForm, account_number: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Date of Signing</label><input type="date" style={inputStyle} value={savingsForm.signed_date} onChange={e => setSavingsForm({...savingsForm, signed_date: e.target.value})} /></div>
            <button style={btnStyle} onClick={() => openOfficerModal(generateSavingsAgreement)}>Generate Savings Agreement PDF</button>
          </div>
        )}

        {activeDoc === 'lcc' && (
          <div>
            <h3 style={{ color: '#2c3e50', marginTop: 0 }}>Leadership Code & Integrity Declaration — Appendix 2</h3>
            <div style={fieldStyle}><label style={labelStyle}>Leadership Position / Title</label><input style={inputStyle} value={lccForm.position_title} onChange={e => setLccForm({...lccForm, position_title: e.target.value})} placeholder="e.g. Finance Chairperson" /></div>
            <div style={fieldStyle}><label style={labelStyle}>Election / Appointment Date</label><input type="date" style={inputStyle} value={lccForm.election_date} onChange={e => setLccForm({...lccForm, election_date: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Term Start Date</label><input type="date" style={inputStyle} value={lccForm.term_start} onChange={e => setLccForm({...lccForm, term_start: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Term End Date</label><input type="date" style={inputStyle} value={lccForm.term_end} onChange={e => setLccForm({...lccForm, term_end: e.target.value})} /></div>
            <div style={fieldStyle}><label style={labelStyle}>Agreement Date</label><input type="date" style={inputStyle} value={lccForm.agreement_date} onChange={e => setLccForm({...lccForm, agreement_date: e.target.value})} /></div>
            <button style={btnStyle} onClick={() => openOfficerModal(generateLCC)}>Generate LCC PDF</button>
          </div>
        )}

      </div>
    </div>
  );
}

export default Documents;